/**
 * Typed backend client.
 *
 * Eden derives call signatures and response types from the Elysia app's
 * TypeBox schemas, so a route change is a compile error here rather than a
 * runtime surprise. The two hand-written calls are the SSE stream and the
 * upload, because streaming request/response bodies is outside what Eden
 * models — both still go through the same shared types.
 */

import { treaty } from "@elysiajs/eden";
import type { App } from "@catchup/server/src/index.ts";
import type { Chart, SessionAttempt } from "@catchup/shared";
import type { JobEvent } from "@catchup/server/src/queue.ts";

const base = import.meta.env.DEV ? "http://127.0.0.1:5173" : window.location.origin;

export const api = treaty<App>(base);

/* --------------------------------------------------------- passphrase */

const KEY_STORAGE = "catchup:key";

export function storedKey(): string {
  try {
    return localStorage.getItem(KEY_STORAGE) ?? "";
  } catch {
    return "";
  }
}

export function setStoredKey(key: string): void {
  try {
    if (key) localStorage.setItem(KEY_STORAGE, key);
    else localStorage.removeItem(KEY_STORAGE);
  } catch {
    /* private browsing */
  }
}

function authHeaders(): Record<string, string> {
  const key = storedKey();
  return key ? { authorization: `Bearer ${key}` } : {};
}

export async function checkKey(key: string): Promise<{ ok: boolean; error: string }> {
  const res = await fetch(`${base}/api/auth/check`, {
    method: "POST",
    headers: key ? { authorization: `Bearer ${key}` } : {},
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return { ok: res.ok, error: body.error ?? (res.ok ? "" : "Rejected") };
}

/* -------------------------------------------------------------- reads */

export async function fetchHealth() {
  const { data, error } = await api.api.health.get();
  if (error) throw new Error(String(error.value));
  return data;
}

export async function fetchConfig() {
  const { data, error } = await api.api.config.get();
  if (error) throw new Error(String(error.value));
  return data;
}

export interface LibraryEntry {
  fileHash: string;
  artist: string;
  title: string;
  album?: string | null;
  duration: number;
  source: string;
  flags: string[];
  words: number;
  playableSections: number;
  createdAt: string;
  hasAudio: boolean;
  coverArtUrl?: string | null;
}

export async function fetchLibrary(refresh = false): Promise<LibraryEntry[]> {
  const res = await fetch(`${base}/api/library${refresh ? "?refresh=1" : ""}`);
  if (!res.ok) throw new Error("Could not load the library.");
  const body = (await res.json()) as { entries: LibraryEntry[] };
  return body.entries;
}

export async function fetchChart(hash: string): Promise<Chart> {
  const res = await fetch(`${base}/api/charts/${hash}`);
  if (!res.ok) throw new Error("No chart for that track.");
  return (await res.json()) as Chart;
}

/** Audio always comes from our own origin — see CDN_REDIRECT in the server. */
export function audioUrl(hash: string): string {
  return `${base}/api/audio/${hash}`;
}

export async function logAttempt(attempt: SessionAttempt) {
  const { data, error } = await api.api.sessions.post(attempt as never);
  if (error) throw new Error(String(error.value));
  return data;
}

/* ------------------------------------------------------------- writes */

export interface UploadResult {
  fileHash: string;
  bytes: number;
  deduped: boolean;
  hasChart: boolean;
  filename: string;
}

/** Upload with progress. XHR rather than fetch, which has no upload progress. */
export function uploadTrack(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${base}/api/upload?filename=${encodeURIComponent(file.name)}`);
    const key = storedKey();
    if (key) xhr.setRequestHeader("authorization", `Bearer ${key}`);
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: Record<string, unknown> = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON error page */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as unknown as UploadResult);
      else reject(new Error((body.error as string) ?? `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed — network error."));
    xhr.send(file);
  });
}

export interface ChartRequest {
  fileHash: string;
  artist?: string;
  title?: string;
  album?: string;
  lrcOnly?: boolean;
  noCache?: boolean;
}

/** Stream a charting job. Yields queue position, then worker progress. */
export async function* streamChart(
  req: ChartRequest,
  signal?: AbortSignal,
): AsyncGenerator<JobEvent> {
  const res = await fetch(`${base}/api/chart`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(req),
    signal,
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Charting failed (${res.status})`);
  }
  if (!res.body) throw new Error("No response stream");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() ?? "";
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      yield JSON.parse(line.slice(5).trim()) as JobEvent;
    }
  }
}

export async function deleteTrack(hash: string): Promise<boolean> {
  const res = await fetch(`${base}/api/library/${hash}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return res.ok;
}
